import React, { KeyboardEvent, PointerEvent, useRef, useState } from 'react';
import { ChevronsLeftRightIcon } from 'lucide-react';
type BeforeAfterSliderProps = {
  before: string;
  after: string;
  label: string;
};
export function BeforeAfterSlider({
  before,
  after,
  label
}: BeforeAfterSliderProps) {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  function updateFromClientX(clientX: number) {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition(clamp((clientX - rect.left) / rect.width * 100));
  }
  function handlePointerDown(event: PointerEvent<HTMLDivElement>) {
    draggingRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    updateFromClientX(event.clientX);
  }
  function handlePointerMove(event: PointerEvent<HTMLDivElement>) {
    if (draggingRef.current) updateFromClientX(event.clientX);
  }
  function stopDragging() {
    draggingRef.current = false;
  }
  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const step = event.shiftKey ? 10 : 5;
    const keyMap: Record<string, number> = {
      ArrowLeft: position - step,
      ArrowDown: position - step,
      ArrowRight: position + step,
      ArrowUp: position + step,
      Home: 0,
      End: 100
    };
    if (event.key in keyMap) {
      event.preventDefault();
      setPosition(clamp(keyMap[event.key]));
    }
  }
  return <div ref={containerRef} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={stopDragging} onPointerCancel={stopDragging} className="relative aspect-[3/2] w-full cursor-ew-resize touch-pan-y select-none overflow-hidden rounded-lg bg-black" data-id="element-30" data-mp="1wb5it7oisih8">
      <img src={after} alt={`${label} after repair`} draggable={false} className="absolute inset-0 h-full w-full object-cover" data-id="element-31" data-mp="6xahgjlx8c75" />
      <img src={before} alt={`${label} before repair`} draggable={false} className="absolute inset-0 h-full w-full object-cover" style={{
      clipPath: `inset(0 ${100 - position}% 0 0)`
    }} data-id="element-32" data-mp="3uw7wmzq1yuj" />

      <span className="pointer-events-none absolute left-4 top-4 rounded bg-ink/75 px-2.5 py-1 text-xs font-semibold text-white" data-id="element-33" data-mp="dtbz0pyr1841">
        Before
      </span>
      <span className="pointer-events-none absolute right-4 top-4 rounded bg-surface/90 px-2.5 py-1 text-xs font-semibold text-ink" data-id="element-34" data-mp="8wbs6cu2s1bv">
        After
      </span>

      <div className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)]" style={{
      left: `${position}%`
    }} data-id="element-35" data-mp="irdj9e7wvdh3">
        <div role="slider" tabIndex={0} aria-label={`Compare before and after: ${label}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(position)} aria-valuetext={`${Math.round(position)}% before`} onKeyDown={handleKeyDown} className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-accent shadow-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/50" data-id="element-36" data-mp="rjyorsmj7nnz">
          <ChevronsLeftRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-37" data-mp="1g5d2nd9h135v" />
        </div>
      </div>
    </div>;
}
function clamp(value: number): number {
  return Math.min(100, Math.max(0, value));
}